import { lazy, type ComponentType, type LazyExoticComponent } from "react";

/**
 * Guide discovery.
 *
 * Guides are MDX files under `content/<version>/guides/`. Each exports its own
 * metadata, so adding a guide is a single new file -- no manifest to keep in
 * sync, which is the point of the authoring workflow:
 *
 *   export const meta = { title: "Errors", order: 5, summary: "..." };
 *
 * That metadata is lifted out at build time rather than imported here. An eager
 * glob for the `meta` export would count as a static import of the whole
 * module, and Rollup would then refuse to split the compiled bodies -- which are
 * large, because Shiki emits a styled span per token.
 */
export interface GuideMeta {
  slug: string;
  title: string;
  order: number;
  summary?: string;
}

export interface Guide extends GuideMeta {
  version: string;
  Component: LazyExoticComponent<ComponentType>;
}

/** Discovered by glob, so a new version needs no registration here. */
const keyed = <T>(
  modules: Record<string, T>,
  prefix: string,
): Record<string, T> =>
  Object.fromEntries(
    Object.entries(modules).flatMap(([path, value]) => {
      const match = new RegExp(`/${prefix}\\.(.+)\\.json$`).exec(path);
      return match ? [[match[1], value] as const] : [];
    }),
  );

const GUIDE_INDEX = keyed(
  import.meta.glob<GuideMeta[]>("/src/generated/guides.*.json", {
    eager: true,
    import: "default",
  }),
  "guides",
);

const GUIDE_TEXT = keyed(
  import.meta.glob<Record<string, string>>("/src/generated/guide-text.*.json", {
    eager: true,
    import: "default",
  }),
  "guide-text",
);

/** Lazy only. Every guide body becomes its own chunk. */
const componentModules = import.meta.glob<{ default: ComponentType }>(
  "/content/*/guides/*.mdx",
);

/** `/content/v1/guides/03-errors.mdx` -> `{ version: "v1", slug: "errors" }` */
function parsePath(filePath: string): { version: string; slug: string } | null {
  const match = /^\/content\/([^/]+)\/guides\/(.+)\.mdx$/.exec(filePath);
  if (!match) return null;
  return {
    version: match[1],
    // A leading `NN-` orders files on disk only; keep it out of the URL.
    slug: match[2].replace(/^\d+[-_]/, ""),
  };
}

const loaders = new Map<string, () => Promise<{ default: ComponentType }>>();
for (const [filePath, loader] of Object.entries(componentModules)) {
  const parsed = parsePath(filePath);
  if (parsed) loaders.set(`${parsed.version}/${parsed.slug}`, loader);
}

const components = new Map<string, LazyExoticComponent<ComponentType>>();

function componentFor(
  version: string,
  slug: string,
): LazyExoticComponent<ComponentType> | undefined {
  const key = `${version}/${slug}`;
  const cached = components.get(key);
  if (cached) return cached;

  const loader = loaders.get(key);
  if (!loader) return undefined;

  const component = lazy(loader);
  components.set(key, component);
  return component;
}

export function guidesFor(version: string): Guide[] {
  return (GUIDE_INDEX[version] ?? []).flatMap((meta) => {
    const Component = componentFor(version, meta.slug);
    return Component ? [{ ...meta, version, Component }] : [];
  });
}

export const findGuide = (version: string, slug: string): Guide | undefined =>
  guidesFor(version).find((g) => g.slug === slug);

/**
 * Guide prose, stripped of MDX syntax, for the search index. Also produced by
 * `pnpm content`, for the same chunk-splitting reason.
 */
export function guideText(version: string, slug: string): string {
  return GUIDE_TEXT[version]?.[slug] ?? "";
}
